/*
 * Complementary products on the product page.
 *
 * <complementary-products> loads the Search & Discovery complementary
 * recommendations through section rendering and swaps them in. Unlike the
 * generic <product-recommendations> element it does not gate the fetch on an
 * IntersectionObserver: the placeholder is empty, so it has a zero-area box and
 * a jump to the bottom of the page can skip the intersection entirely, leaving
 * the block permanently empty.
 *
 * Each slide renders the theme's standard product card, so the size choice and
 * the add to cart are the site's quick buy modal, wired by <quick-view-*>.
 */
if (!customElements.get('complementary-products')) {
  class ComplementaryProducts extends HTMLElement {
    connectedCallback() {
      if (!this.onQuickViewOpen) {
        this.onQuickViewOpen = this.liftQuickView.bind(this);
        // Capture phase: runs before the drawer's own summary handler opens it,
        // for the "+" and for "Choose options" (which clicks that summary).
        this.addEventListener('click', this.onQuickViewOpen, true);
      }

      if (this.loaded || !this.dataset.url) return;
      this.loaded = true;

      const load = () => this.loadRecommendations();
      if ('requestIdleCallback' in window) {
        requestIdleCallback(load, { timeout: 1500 });
      } else {
        setTimeout(load, 0);
      }
    }

    loadRecommendations() {
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          // Parse once into an inert <template>: its content is not upgraded,
          // so no throwaway copy of the slider is ever constructed. Moving the
          // nodes into this element upgrades them once, in the document, which
          // is what lets the slider initialise.
          const parsed = document.createElement('template');
          parsed.innerHTML = text;
          const recommendations = parsed.content.querySelector('complementary-products');
          if (recommendations && recommendations.innerHTML.trim().length) {
            this.useOwnSlider(recommendations);
            this.replaceChildren(...recommendations.childNodes);
            this.watchOverflow();
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }

    /*
     * Swap the fetched <slider-component> for <complementary-slider>, a subclass
     * of the theme's own slider that wires its controls as soon as it is in the
     * document.
     *
     * The shared SliderComponent defers init() - the click handlers on the two
     * arrows, the scroll listener and the ResizeObserver - behind
     * theme.initWhenVisible(), an IntersectionObserver on the component itself.
     * That is right for a section that is part of the first render, but this row
     * sits deep in the product info column (~2400px down the page at 768px) and
     * is injected after load, so it is wired only once the shopper has scrolled
     * it into view. An arrow pressed in the same frame that reveals it - the
     * browser's own scroll-into-view on a keyboard Tab to the arrow, a tap at the
     * end of a fling - is pressed before any handler exists, and does nothing;
     * the next press works. The controls of a block we inject ourselves should
     * be live the moment they are on the page.
     *
     * Subclass rather than fork: init() is the only member overridden, purely to
     * make it idempotent, so the visibility callback that the base constructor
     * still schedules cannot wire a second click handler on top of ours (two
     * handlers would scroll two slides per press wherever scrolling is
     * instant, e.g. under prefers-reduced-motion). Every other slider on the
     * site keeps the shared element and its deferred init untouched.
     */
    useOwnSlider(recommendations) {
      const slider = recommendations.querySelector('slider-component');
      const Base = customElements.get('slider-component');
      // Without the base class there is nothing to extend: leave the markup as
      // rendered and let the shared element behave as it always has.
      if (!slider || !Base) return;

      if (!customElements.get('complementary-slider')) {
        customElements.define(
          'complementary-slider',
          class ComplementarySlider extends Base {
            connectedCallback() {
              if (typeof super.connectedCallback === 'function') super.connectedCallback();
              // The base constructor gives up when there is nothing to slide
              // (a single recommendation renders no controls); so do we.
              if (this.slider && this.nextButton && this.prevButton) this.init();
            }

            init() {
              if (this.initialised) return;
              this.initialised = true;
              super.init();
            }
          }
        );
      }

      // Built in the template's inert document: an element created in the live
      // document would upgrade - and so run the base constructor, which reads
      // its slides and controls - while it is still empty.
      const own = recommendations.ownerDocument.createElement('complementary-slider');
      Array.from(slider.attributes).forEach((attribute) => own.setAttribute(attribute.name, attribute.value));
      own.append(...slider.childNodes);
      slider.replaceWith(own);
    }

    /*
     * The slide controls are rendered whenever there is more than one product,
     * but when every card already fits the row there is nothing to scroll: the
     * theme's slider leaves both arrows enabled (the slide widths can sum to
     * within a pixel of the row) and clicking them does nothing. Hide the group
     * while the row does not overflow, and bring it back when it does.
     *
     * The same observer keeps the injected <slider-component> measured. The
     * theme's SliderComponent works out its scroll step in initPages() from the
     * offset between the first two slides, and only re-measures from a
     * ResizeObserver on the scrolling row itself. These slides arrive after page
     * load: the first measurement can land before the cards have their final
     * layout - while the row still measures zero, initPages() finds no slide
     * with a width, returns before it has a step and never calls update() - and
     * the row's own border box need not change afterwards, so nothing asks the
     * slider to measure again. The first arrow click then scrolled by an
     * unmeasured step, i.e. nowhere, and the arrow states stayed at their markup
     * defaults until that dead scroll finally ran update(). Re-measuring here,
     * once the injected cards have settled, is driven entirely from this block:
     * resetPages() is the slider's own public entry point and no shared slider
     * behaviour is changed.
     */
    watchOverflow() {
      this.row = this.querySelector('.complementary-products__row');
      this.controls = this.querySelector('.complementary-products__controls');
      this.slider = this.querySelector('complementary-slider, slider-component');
      if (!this.row) return;
      if (!this.onSettled) this.onSettled = this.scheduleSync.bind(this);

      this.sync();

      // The element may still be waiting for its definition when the markup is
      // swapped in; resetPages() only exists once it has upgraded.
      customElements.whenDefined(this.slider ? this.slider.localName : 'slider-component').then(() => this.sync());

      // A card whose image has not decoded yet is not at its final size. Each
      // one that finishes re-measures; already complete images cost nothing.
      this.row.querySelectorAll('img').forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', this.onSettled, { once: true });
        image.addEventListener('error', this.onSettled, { once: true });
      });

      if (this.rowObserver) this.rowObserver.disconnect();
      if ('ResizeObserver' in window) {
        this.rowObserver = new ResizeObserver(this.onSettled);
        this.rowObserver.observe(this.row);
        // The row can keep its box while a slide inside it changes width, which
        // is exactly the case that left the step stale.
        const firstSlide = this.row.querySelector('.complementary-products__item');
        if (firstSlide) this.rowObserver.observe(firstSlide);
      } else if (!this.resizeHandler) {
        this.resizeHandler = this.onSettled;
        window.addEventListener('resize', this.resizeHandler);
      }
    }

    // Coalesce every trigger into one measurement per frame: no polling, and no
    // observer feedback loop, since nothing this touches resizes the row.
    scheduleSync() {
      if (this.syncFrame) return;
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.sync();
      });
    }

    sync() {
      this.syncControls();
      // resetPages() re-reads the slides, re-measures the step and runs the
      // slider's own update(), which is what puts the arrows in the right state.
      if (this.slider && typeof this.slider.resetPages === 'function') this.slider.resetPages();
    }

    syncControls() {
      if (!this.row || !this.controls) return;
      this.controls.toggleAttribute('hidden', this.row.scrollWidth <= this.row.clientWidth + 1);
    }

    /*
     * Open each card's quick buy from where a homepage card's opens.
     *
     * The standard card carries its own <quick-view-drawer>, whose modal is a
     * `position: fixed; z-index: 100` layer meant to sit above the page backdrop
     * (`.transition-body::after`, z-index 30). On a homepage section nothing
     * between the card and #MainContent forms a stacking context, so it does.
     * This row is inside the product info column, which is `position: sticky;
     * z-index: 1` from 750px: that traps the modal's z-index below the backdrop,
     * which then covers it and swallows every click. Nested in the PDP's markup
     * it also inherits the PDP's descendant-scoped product styles and bubbles
     * its events through the PDP's own elements - the "does not look right" on
     * a phone.
     *
     * So, as the shopper opens it, move the card's whole drawer (the element the
     * theme's MenuDrawer logic owns, focus trap and all) to a host at the end of
     * #MainContent - the same ancestors a homepage card's modal has - and put it
     * back in its card once the theme's close animation has finished. The quick
     * buy itself, its backdrop and its add to cart are the theme's, untouched.
     */
    liftQuickView(event) {
      const summary = event.target.closest && event.target.closest('.quick-view__summary');
      const drawer = summary && summary.closest('quick-view-drawer');
      if (!drawer || !this.contains(drawer)) return;
      const details = drawer.querySelector('details');
      if (!details || details.hasAttribute('open')) return;

      const host = ComplementaryProducts.quickViewHost();
      const placeholder = document.createComment('complementary-products quick view');
      drawer.replaceWith(placeholder);
      host.append(drawer);

      const observer = new MutationObserver(() => {
        if (details.hasAttribute('open')) return;
        observer.disconnect();
        const hadFocus = drawer.contains(document.activeElement);
        if (placeholder.isConnected) {
          placeholder.replaceWith(drawer);
        } else {
          drawer.remove();
        }
        if (hadFocus && summary.isConnected) summary.focus({ preventScroll: true });
      });
      observer.observe(details, { attributes: true, attributeFilter: ['open'] });
    }

    static quickViewHost() {
      let host = document.getElementById('ComplementaryQuickViewHost');
      if (!host) {
        host = document.createElement('div');
        host.id = 'ComplementaryQuickViewHost';
        host.className = 'complementary-products__quick-view-host';
        (document.getElementById('MainContent') || document.body).append(host);
      }
      return host;
    }

    disconnectedCallback() {
      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      if (this.rowObserver) this.rowObserver.disconnect();
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
    }
  }

  customElements.define('complementary-products', ComplementaryProducts);
}
